import { MarketNightlyRefreshProviderSet, ProviderSetStatusFilter, ProviderSetStatusCounts } from "@app/operations/market-nightly-refresh/interfaces";
import cx from "classnames";
import { FC } from "react";

export const isProviderSetIgnored = (provider: MarketNightlyRefreshProviderSet): boolean =>
  provider.overrideType === "Exclude" ||
  isSystemIgnored(provider);

export const isSystemIgnored = (provider: MarketNightlyRefreshProviderSet): boolean =>
  provider.isLowUsage && provider.overrideType !== "Include";

export const isProviderSetRunning = (provider: MarketNightlyRefreshProviderSet): boolean =>
  provider.status === "Processing" || provider.status === "Pending";

interface filterProviderSetsByPillProps {
  providers: MarketNightlyRefreshProviderSet[],
  activeFilter: ProviderSetStatusFilter,
}

export const filterProviderSetsByPill = ({ providers, activeFilter }: filterProviderSetsByPillProps)
  : MarketNightlyRefreshProviderSet[] =>
  providers.filter((p) => {
    switch (activeFilter) {
      case "all": return true
      case "active": return !isProviderSetIgnored(p);
      case "system-ignored": return isSystemIgnored(p);
      case "ignored": return isProviderSetIgnored(p);
    }
  });

interface filterProviderSetsBySearchProps {
  providers: MarketNightlyRefreshProviderSet[],
  search: string,
}

export const filterProviderSetsBySearch = ({ providers, search }: filterProviderSetsBySearchProps)
  : MarketNightlyRefreshProviderSet[] => {
  const term = search.trim().toLowerCase();
  if (!term) return providers;

  return providers.filter((p) =>
    p.name.toLowerCase().includes(term) ||
    String(p.providerSetId).includes(term) ||
    String(p.providerSetVersionId).includes(term)
  );
};

interface filterProviderSetsProps {
  providers: MarketNightlyRefreshProviderSet[],
  activeFilter: ProviderSetStatusFilter,
  search: string
}

export const filterProviderSets = ({ providers, activeFilter, search }: filterProviderSetsProps)
  : MarketNightlyRefreshProviderSet[] =>
  filterProviderSetsBySearch({ providers: filterProviderSetsByPill({ activeFilter, providers }), search });

export const countProviderSetsByFilter = (providers: MarketNightlyRefreshProviderSet[]): ProviderSetStatusCounts => {
  const ignoredCount = providers.filter(isProviderSetIgnored).length;
  return {
    active: providers.length - ignoredCount,
    all: providers.length,
    ignored: ignoredCount,
    systemIgnored: providers.filter(isSystemIgnored).length
  }
}

interface ProviderSetFilterPillConfig {
  countKey: keyof ProviderSetStatusCounts;
  filter: ProviderSetStatusFilter;
  label: string;
}

const PILL_CONFIG: ProviderSetFilterPillConfig[] = [
  { countKey: "all", filter: "all", label: "All" },
  { countKey: "active", filter: "active", label: "Active" },
  { countKey: "systemIgnored", filter: "system-ignored", label: "System-ignored" },
  { countKey: "ignored", filter: "ignored", label: "Ignored" },
];

interface ProviderSetFilterPillsProps {
  activeFilter: ProviderSetStatusFilter,
  counts: ProviderSetStatusCounts,
  onFilterChange: (filter: ProviderSetStatusFilter) => void;
}

export const ProviderSetFilterPills: FC<ProviderSetFilterPillsProps> = ({
  activeFilter,
  counts,
  onFilterChange,
}) => {
  return (
    <div className="market-nightly-refresh__filter-pills" role="tablist">
      {PILL_CONFIG.map(({ countKey, filter, label }) => {
        const isActive = filter === activeFilter;
        return (
          <button
            key={filter}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={cx("market-nightly-refresh__filter-pill", isActive && "market-nightly-refresh__filter-pill--active")}
            onClick={() => onFilterChange(filter)}
            data-testid={`mnr-filter-pill--${filter}`}
          >
            {label}
            <span
              className={cx("market-nightly-refresh__filter-pill-count", isActive && "market-nightly-refresh__filter-pill-count--active")}>
              {counts[countKey]}
            </span>
          </button>
        )
      })}
    </div>
  )
}
